import { createFileRoute } from "@tanstack/react-router";
import { BackButton } from "@/components/site/BackButton";
import { faqPageJsonLd } from "@/lib/seo";

const RETURNS_FAQ = [
  { question: "What is the KAKAIYS return window?", answer: "You may return or exchange any piece within 14 days of delivery for a full refund or store credit, provided it is unworn, unwashed and in original condition with all tags, dust bag and packaging included." },
  { question: "Are made-to-order pieces returnable?", answer: "No. Made-to-order, bespoke, made-to-measure, personalised and monogrammed pieces are final sale and not eligible for return or exchange." },
  { question: "How do I start a return?", answer: "Email returns@kakaiys.com within 14 days of receiving your order with your order number and reason. We will issue a Return Authorisation (RA) number and shipping instructions within one business day." },
  { question: "When will I receive my refund?", answer: "Once your return is received and inspected (typically within 3 business days), we issue your refund to the original payment method. Refunds usually appear in 5 to 10 business days depending on your bank or card issuer." },
  { question: "Who pays for return shipping?", answer: "Return shipping is the customer's responsibility unless the item is defective or we shipped the wrong piece. We strongly recommend a tracked, insured courier." },
  { question: "Is there a craftsmanship warranty?", answer: "Yes. Every KAKAIYS piece carries a 6-month craftsmanship warranty from the date of delivery, covering manufacturing defects in stitching, hardware and lining under normal use." },
];

export const Route = createFileRoute("/returns")({
  head: () => ({
    meta: [
      { title: "Returns & Exchanges — KAKAIYS" },
      {
        name: "description",
        content:
          "Free 14-day returns and exchanges on every KAKAIYS piece. Read our hassle-free return policy and how to start a return.",
      },
      { property: "og:title", content: "Returns & Exchanges — KAKAIYS" },
      {
        property: "og:description",
        content: "14-day returns and exchanges on every KAKAIYS leather piece.",
      },
      { property: "og:url", content: "https://kakaiys.com/returns" },
    ],
    links: [{ rel: "canonical", href: "https://kakaiys.com/returns" }],
    scripts: [
      { type: "application/ld+json", children: faqPageJsonLd(RETURNS_FAQ) },
    ],
  }),
  component: ReturnsPage,
});

function ReturnsPage() {
  return (
    <article className="pt-40 pb-24 container-luxe max-w-3xl">
      <BackButton fallback="/" className="mb-8" />
      <div className="eyebrow mb-5">Maison · Returns</div>
      <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-12">Returns &amp; Exchanges</h1>

      <div className="space-y-10 text-foreground/85">
        <section>
          <h2 className="font-serif text-2xl mb-3">Our promise</h2>
          <p>
            We want you to love your KAKAIYS piece. If for any reason it is not right, you may return or
            exchange it within <strong>14 days of delivery</strong> for a full refund or store credit.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Return conditions</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>Item must be unworn, unwashed and in original condition.</li>
            <li>All original tags, dust bag and packaging must be included.</li>
            <li>Item must show no signs of wear, fragrance, smoke or alteration.</li>
            <li>Made-to-order and personalised pieces are <strong>final sale</strong> and not eligible for return.</li>
            <li>Sale items marked "Final Sale" are not eligible for return or exchange.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">How to start a return</h2>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Email <a className="text-gold underline-grow" href="mailto:returns@kakaiys.com">returns@kakaiys.com</a> within 14 days of receiving your order.</li>
            <li>Include your order number and the reason for return.</li>
            <li>We will issue a Return Authorisation (RA) number and shipping instructions within 1 business day.</li>
            <li>Pack the item securely with the RA number visible on the outside of the parcel.</li>
            <li>Ship via a tracked, insured service to the address provided.</li>
          </ol>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Refunds</h2>
          <p>
            Once your return is received and inspected (typically within 3 business days), we will issue
            your refund to the original payment method. Refunds usually appear in your account within
            <strong> 5–10 business days</strong> depending on your bank or card issuer.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Exchanges</h2>
          <p>
            Need a different size or colour? Follow the same process and indicate "Exchange" in your email.
            We will hold the new item for you and ship it as soon as your return is received. Exchanges
            within the same price tier ship complimentary.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Return shipping</h2>
          <p>
            Return shipping is the customer's responsibility unless the item is defective or we shipped the
            wrong piece. We strongly recommend a tracked, insured courier — we cannot process refunds for
            parcels lost in transit.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Defective or incorrect items</h2>
          <p>
            If you received a defective or incorrect item, contact us at
            <a className="text-gold underline-grow" href="mailto:care@kakaiys.com"> care@kakaiys.com</a> within 7 days of delivery
            with photos. We will arrange complimentary return shipping and a full refund or replacement.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Craftsmanship warranty</h2>
          <p>
            Every KAKAIYS piece carries a <strong>6-month craftsmanship warranty</strong> from the date of
            delivery, covering manufacturing defects in stitching, hardware and lining under normal use. The
            warranty does not cover: natural patina, water or oil staining, scratches and marks from daily
            wear, damage from misuse, improper storage, exposure to extreme heat or chemicals, or
            alterations made by a third party. Contact <a className="text-gold underline-grow" href="mailto:care@kakaiys.com">care@kakaiys.com</a> with
            photos and your order number to make a warranty claim.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Handcrafted variations</h2>
          <p>
            Every piece is cut, stitched and finished by hand from full-grain leather. Slight variations in
            colour, grain, scarring, stitch alignment and patina are inherent to natural leather and are
            <strong> not considered defects</strong>. These characteristics are part of what makes each
            KAKAIYS piece one of a kind.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Non-returnable items</h2>
          <p>The following are strictly final sale and cannot be returned, exchanged or refunded:</p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>Made-to-order, bespoke and made-to-measure pieces</li>
            <li>Personalised or monogrammed items</li>
            <li>Custom colour, custom leather or custom hardware orders</li>
            <li>Items marked "Final Sale" at checkout</li>
            <li>Care products, gift cards and promotional items</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Chargebacks &amp; disputes</h2>
          <p>
            We are committed to resolving every issue directly. Please contact us before initiating a
            chargeback or payment dispute — most issues are resolved within 48 hours. Chargebacks filed
            without first contacting us, or for items that fall outside this Returns Policy, will be
            contested with full order, delivery and communication records.
          </p>
        </section>
      </div>
    </article>
  );
}
